import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, Crown, Shield, User as UserIcon, CheckCircle2, AlertTriangle,
  Loader2, ArrowRight, LogIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_INFO = {
  owner: { icon: Crown, label: 'Owner', desc: 'Full control including billing.' },
  admin: { icon: Shield, label: 'Admin', desc: 'Manage members + integrations.' },
  member: { icon: UserIcon, label: 'Member', desc: 'Read & use features.' },
};

const AcceptInvitationPage = () => {
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(null);

  const userToken = localStorage.getItem('token');
  const userEmail = localStorage.getItem('userEmail');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/org/invitations/preview/${inviteToken}`);
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || 'Invitation not found or expired');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [inviteToken]);

  const acceptInvitation = async () => {
    if (!userToken) {
      // Save intent and bounce to login
      sessionStorage.setItem('post_login_redirect', `/invite/${inviteToken}`);
      navigate('/login', { replace: false });
      return;
    }
    setAccepting(true);
    try {
      const res = await axios.post(
        `${API}/org/invitations/${inviteToken}/accept`,
        {},
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      setAccepted(res.data);
      toast.success(res.data.message || 'Welcome aboard!');
      // Auto-redirect to dashboard after short delay so user sees confirmation
      setTimeout(() => navigate('/dashboard'), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not accept invitation');
    } finally {
      setAccepting(false);
    }
  };

  // ── Render states ─────────────────────────────────────────────────────────
  // White page + dark floating card aesthetic (matches /login)
  const Hero = ({ children }) => (
    <div
      className="min-h-screen relative overflow-hidden"
      data-testid="accept-invite-page"
      style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(16, 185, 129, 0.08) 0%, transparent 50%), radial-gradient(ellipse 60% 50% at 90% 100%, rgba(6, 182, 212, 0.06) 0%, transparent 50%), #ffffff'
      }}
    >
      <div className="absolute top-[-200px] left-[-200px] w-[600px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-200px] right-[-200px] w-[600px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-3xl pointer-events-none" />
      <div className="relative max-w-md mx-auto px-4 pt-16 pb-12">
        <div className="dark relative">
          <div className="absolute -inset-1 bg-emerald-500/15 rounded-3xl blur-2xl pointer-events-none" />
          <div className="relative rounded-3xl bg-slate-950 border border-emerald-500/20 shadow-2xl shadow-emerald-500/10 overflow-hidden text-white">
            <div className="h-1 bg-emerald-500" />
            <div className="p-8">
              {children}
            </div>
          </div>
        </div>
        <div className="text-center mt-6">
          <Link to="/" className="text-xs text-slate-500 hover:text-slate-700 transition-colors">
            CARTIEAI · Financial OS for Cloud
          </Link>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Hero>
        <div className="text-center py-4">
          <Loader2 className="w-8 h-8 mx-auto mb-3 text-emerald-400 animate-spin" />
          <p className="text-slate-400 text-sm">Loading invitation…</p>
        </div>
      </Hero>
    );
  }

  if (error) {
    const isAlreadyAccepted = /already accepted/i.test(error);
    return (
      <Hero>
        <div className="text-center">
          {isAlreadyAccepted ? (
            <CheckCircle2 className="w-10 h-10 mx-auto mb-4 text-emerald-400" />
          ) : (
            <AlertTriangle className="w-10 h-10 mx-auto mb-4 text-rose-400" />
          )}
          <h1 className="text-xl font-bold mb-2 text-white">
            {isAlreadyAccepted ? 'Already accepted' : 'Invitation unavailable'}
          </h1>
          <p className="text-sm text-slate-400 mb-6">
            {isAlreadyAccepted
              ? 'This invitation has already been accepted. Sign in to access the organization.'
              : error}
          </p>
          <div className="flex justify-center gap-2">
            {isAlreadyAccepted && (
              <Link to="/login">
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="signin-already-accepted-btn">
                  <LogIn className="w-4 h-4 mr-1.5" />
                  Sign in
                </Button>
              </Link>
            )}
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="back-home-btn">
                Back to home
              </Button>
            </Link>
          </div>
        </div>
      </Hero>
    );
  }

  if (accepted) {
    return (
      <Hero>
        <div className="text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
          <h1 className="text-2xl font-bold mb-2 text-white">
            <span className="text-emerald-400">
              Welcome aboard!
            </span>
          </h1>
          <p className="text-sm text-slate-400 mb-6">
            You've joined <strong className="text-white">{accepted.organization_name}</strong> as a <strong>{accepted.role}</strong>.
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="goto-dashboard-btn"
          >
            Go to Dashboard
            <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </Hero>
    );
  }

  const roleInfo = ROLE_INFO[preview.role] || ROLE_INFO.member;
  const RoleIcon = roleInfo.icon;
  const orgName = preview.organization?.name || 'an organization';
  const emailMismatch = userEmail && userEmail.toLowerCase() !== preview.email.toLowerCase();

  return (
    <Hero>
      <div className="text-center mb-6">
        <div className="inline-flex p-3 rounded-2xl bg-emerald-500 mb-4 shadow-lg shadow-emerald-500/30">
          <Building2 className="w-6 h-6 text-slate-950" />
        </div>
        <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[11px] font-bold tracking-widest mb-3">
          YOU'RE INVITED
        </Badge>
        <h1 className="text-2xl font-bold mb-2 text-white">
          Join <span className="text-emerald-400">{orgName}</span>
        </h1>
        <p className="text-sm text-slate-400">
          You've been invited to <strong className="text-slate-200">{orgName}</strong> on CARTIEAI.
        </p>
      </div>

      <div className="space-y-3 mb-6">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
          <RoleIcon className="w-5 h-5 text-emerald-400 shrink-0" />
          <div className="min-w-0">
            <div className="font-semibold text-white">{roleInfo.label}</div>
            <div className="text-xs text-slate-400">{roleInfo.desc}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
          <div className="w-5 text-center text-slate-400">@</div>
          <div className="min-w-0">
            <div className="font-mono text-sm truncate text-white">{preview.email}</div>
            <div className="text-xs text-slate-400">Your invitation is tied to this email.</div>
          </div>
        </div>
      </div>

      {emailMismatch && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-300 flex gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            You're signed in as <strong>{userEmail}</strong> but this invite is for <strong>{preview.email}</strong>.
            Please log out and sign in with the invited email to accept.
          </div>
        </div>
      )}

      {!userToken ? (
        <Button
          onClick={acceptInvitation}
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
          data-testid="signin-to-accept-btn"
        >
          <LogIn className="w-4 h-4 mr-1.5" />
          Sign in to accept
        </Button>
      ) : (
        <Button
          onClick={acceptInvitation}
          disabled={accepting || emailMismatch}
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-11"
          data-testid="accept-invite-btn"
        >
          {accepting ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" />Joining…</> : <>Accept invitation<ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
      )}

      <p className="text-[11px] text-slate-400 text-center mt-4">
        Expires {preview.expires_at ? new Date(preview.expires_at).toLocaleDateString() : 'in 7 days'}.
      </p>
    </Hero>
  );
};

export default AcceptInvitationPage;
